import React from "react";
import {
  Group,
  Text,
  Box,
  Stack,
  Paper,
  UnstyledButton,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";

import { useElapsedTime } from "@/hooks/useElapsedTime";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ActiveTravelIndicatorProps {
  /** Callback when the indicator is clicked */
  onClick: () => void;

  /** Trip start time (ISO string) — used to compute elapsed time */
  startTime: string | undefined;

  /** Vehicle label to display (desktop only) */
  carLabel?: string;

  /** Display variant */
  variant?: "mobile" | "desktop";
}

// ============================================
// Shared Keyframes
// ============================================

const PulseKeyframes: React.FC = () => (
  <style>
    {`
      @keyframes activeTravelPulse {
        0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.5); }
        70% { box-shadow: 0 0 0 5px rgba(22, 163, 74, 0); }
        100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
      }
    `}
  </style>
);

// ============================================
// Component
// ============================================

const ActiveTravelIndicatorComponent: React.FC<ActiveTravelIndicatorProps> = ({
  onClick,
  startTime,
  carLabel,
  variant = "mobile",
}) => {
  const theme = useMantineTheme();
  const elapsedTime = useElapsedTime(startTime);

  if (variant === "mobile") {
    return (
      <UnstyledButton onClick={onClick}>
        <Paper
          px="sm"
          py={6}
          radius="xl"
          style={{
            backgroundColor: "white",
            border: `1px solid ${theme.colors.neutral[2]}`,
          }}
        >
          <Group gap={8} wrap="nowrap">
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: theme.colors.green[6],
                animation: "activeTravelPulse 2s ease-in-out infinite",
              }}
            />
            <Text size="xs" fw={600} c="neutral.7" ff="monospace">
              {elapsedTime}
            </Text>
          </Group>
        </Paper>

        <PulseKeyframes />
      </UnstyledButton>
    );
  }

  // Desktop variant
  return (
    <Tooltip label="Trip in progress - click for details" position="bottom">
      <UnstyledButton onClick={onClick}>
        <Paper
          px="md"
          py="xs"
          radius="md"
          style={{
            backgroundColor: "white",
            border: `1px solid ${theme.colors.neutral[2]}`,
            cursor: "pointer",
            transition: "box-shadow 150ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = `0 0 0 2px ${theme.colors.neutral[2]}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <Group gap="sm" wrap="nowrap">
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: theme.colors.green[6],
                animation: "activeTravelPulse 2s ease-in-out infinite",
              }}
            />
            <Stack gap={0}>
              <Text size="xs" fw={600} c="green.7">
                Trip in Progress
              </Text>
              <Group gap="xs">
                {carLabel && (
                  <Text size="xs" c="neutral.6" lineClamp={1} maw={120}>
                    {carLabel}
                  </Text>
                )}
                <Text size="sm" fw={700} c="neutral.8" ff="monospace">
                  {elapsedTime}
                </Text>
              </Group>
            </Stack>
            <AppIcon iconName="ChevronRight" size={16} color={theme.colors.neutral[4]} />
          </Group>
        </Paper>

        <PulseKeyframes />
      </UnstyledButton>
    </Tooltip>
  );
};

export const ActiveTravelIndicator = React.memo(ActiveTravelIndicatorComponent);